/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Form, FormItemProps } from 'antd';
import { NamePath } from 'rc-field-form/lib/interface';

import './style.less';

export interface FloatingFormItemProps extends FormItemProps {
  children: ReactElement;
  parentFieldName?: string;
}

function FloatingFormItem({
  children,
  parentFieldName,
  style,
  ...formItemProps
}: FloatingFormItemProps) {
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  let inputComponent = React.cloneElement(children, {
    ref: children.props?.allowClear ? ref : undefined,
    autoComplete: 'off',
    className: `${children.props?.className || ''} input-element`,
    size: 'large',

    onFocus: () => setActive(true),
    onBlur: () => {
      if (children.props?.onBlur) {
        children.props.onBlur();
      }
      setActive(false);
    },
  });

  useEffect(() => {
    if (active && inputComponent.props?.disabled) {
      setActive(false);
    }
  }, [active, inputComponent.props]);

  if (children.props.allowClear) {
    inputComponent = React.cloneElement(inputComponent, {
      onClear: ref.current?.blur,
    });
  }

  const getLabelClassName = (
    getFieldValue: (name: NamePath) => unknown,
    getFieldError: (name: NamePath) => string[],
  ): string => {
    let className = 'label';

    let fieldValue;
    let fieldError;

    const fieldName = formItemProps.name as string[];

    if (formItemProps.name) {
      if (parentFieldName) {
        fieldValue = getFieldValue([parentFieldName, ...fieldName]);
      } else {
        fieldValue = getFieldValue(formItemProps.name);
      }

      if (parentFieldName) {
        fieldError = getFieldError([parentFieldName, ...fieldName]);
      } else {
        fieldError = getFieldError(formItemProps.name);
      }
    }

    if (
      fieldValue ||
      fieldValue === 0 ||
      active ||
      !formItemProps.name ||
      fieldValue === false
    )
      className = `${className} --active`;

    if (fieldError && fieldError.length) className = `${className} --error`;

    return className;
  };

  const getFieldErrors = (
    getFieldError: (name: NamePath) => string[],
  ): string[] => {
    let fieldErrors: string[] = [];

    const fieldName = formItemProps.name as string[];

    if (formItemProps.name) {
      if (parentFieldName) {
        fieldErrors = getFieldError([parentFieldName, ...fieldName]);
      } else {
        fieldErrors = getFieldError(fieldName);
      }
    }

    return fieldErrors;
  };

  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue, getFieldError }) => (
        <div
          className="floating-form-item"
          style={style}
          title={formItemProps.label?.toString()}
        >
          <label
            className={getLabelClassName(getFieldValue, getFieldError)}
            htmlFor={formItemProps.name?.toString()}
          >
            {formItemProps.label}
            {formItemProps.rules?.some((item: any) => item.required) ? (
              <span style={{ color: '#CF4A1D' }}>&nbsp;*</span>
            ) : (
              ''
            )}
          </label>
          <Form.Item noStyle {...formItemProps}>
            {inputComponent}
          </Form.Item>
          {getFieldErrors(getFieldError)?.length ? (
            <div className="errors">{getFieldErrors(getFieldError)}</div>
          ) : null}
          {!!formItemProps.extra && formItemProps.extra}
        </div>
      )}
    </Form.Item>
  );
}

export default FloatingFormItem;
